import React from "react";
import formations from "../data/formations";

const FormationCard = ({ formation, index }) => {
  const getIconForLevel = (title) => {
    if (title.includes("Master")) return "🎓";
    if (title.includes("Bachelor") || title.includes("Licence")) return "📚";
    if (title.includes("DUT")) return "🔬";
    if (title.includes("Baccalauréat")) return "🏆";
    return "📜";
  };

  const isCurrentStudy =
    formation.date.includes("2025") || formation.status === "En cours";

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 transition-all duration-200">
      {/* Date en haut en mobile, à droite en desktop */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
        <div className="flex items-center mb-2 sm:mb-0">
          <div className="text-3xl mr-4">
            {getIconForLevel(formation.title)}
          </div>
          <div>
            {/* Date badge en mobile */}
            <div className="sm:hidden mb-2">
              <div
                className={`inline-flex items-center px-3 py-1 rounded-full border ${
                  isCurrentStudy
                    ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800"
                    : "bg-gray-50 border-gray-200 text-gray-700"
                }`}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-medium text-sm">{formation.date}</span>
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-primary transition-colors mb-1">
              {formation.title}
            </h3>
            {formation.complement && (
              <p className="text-sm font-medium text-blue-600 mb-2">
                {formation.complement}
              </p>
            )}
            {/* École et lieu sous le titre, bien séparés */}
            {formation.school && (
              <div className="flex flex-wrap items-center text-gray-600 mb-2 text-sm">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-6a2 2 0 012-2h2a2 2 0 012 2v6"
                  />
                </svg>
                <span className="font-semibold mr-2">{formation.school}</span>
                {formation.lieu && (
                  <span className="text-gray-500">• {formation.lieu}</span>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Date badge en desktop */}
        <div className="hidden sm:block text-right ml-4">
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full border ${
              isCurrentStudy
                ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800"
                : "bg-gray-50 border-gray-200 text-gray-700"
            }`}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="font-medium text-sm">{formation.date}</span>
          </div>
        </div>
      </div>

      {formation.description && (
        <p className="text-gray-700 leading-relaxed mt-2">
          {formation.description}
        </p>
      )}
    </div>
  );
};

const Formations = () => {
  return (
    <div className="pt-24 lg:pt-32 relative overflow-hidden">
      {/* Arrière-plan animé avec bulles décoratives */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>
      <div className="absolute top-32 left-10 w-32 h-32 bg-primary/5 rounded-full animate-pulse"></div>
      <div
        className="absolute top-40 right-20 w-24 h-24 bg-blue-500/5 rounded-full animate-bounce"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute bottom-40 left-20 w-40 h-40 bg-indigo-500/5 rounded-full animate-pulse"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="relative lg:mx-auto lg:max-w-7xl w-full lg:pb-20">
        <div className="lg:px-8 px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="lg:tracking-tight font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-600 to-indigo-600 lg:text-6xl text-4xl mb-6">
              Ma Formation
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Mon parcours académique de l'électronique au développement web
            </p>
          </div>

          {/* Timeline educative */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Parcours Éducatif
            </h2>

            <div className="space-y-6">
              {formations.map((formation, index) => (
                <FormationCard
                  key={formation.id}
                  formation={formation}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Formations;
